/**
 * Verification Popup Component
 * Allows users to verify their Google Business Profile within the platform
 * Supports multiple verification methods: Postcard, Phone, SMS, Email
 *
 * @author Visibeen Team - 2024
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  VerifiedUser,
  CheckCircle,
  ArrowBack,
  Phone,
  Email,
  LocationOn,
  Sms,
  Lock,
  Refresh,
  Info
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import GMBService from '../../../services/GMBService';
import logger from '../../../utils/logger';

// ============================================
// Styled Components
// ============================================

const HeaderContainer = styled(Box)({
  background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
  color: 'white',
  padding: '32px 24px',
  textAlign: 'center',
  position: 'relative'
});

const IconContainer = styled(Box)({
  width: '70px',
  height: '70px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
  backdropFilter: 'blur(10px)'
});

const ContentContainer = styled(Box)({
  padding: '24px'
});

const ActionButton = styled(Button)(({ variant: buttonVariant }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  ...(buttonVariant === 'primary' && {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': { backgroundColor: '#388e3c' },
    '&:disabled': { backgroundColor: '#a5d6a7', color: 'white' }
  }),
  ...(buttonVariant === 'secondary' && {
    backgroundColor: '#f5f5f5',
    color: '#333',
    '&:hover': { backgroundColor: '#e0e0e0' }
  })
}));

const MethodCard = styled(Box)(({ selected }) => ({
  padding: '16px',
  borderRadius: '12px',
  border: selected ? '2px solid #4caf50' : '1px solid #e0e0e0',
  backgroundColor: selected ? '#e8f5e9' : '#fff',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  marginBottom: '12px',
  '&:hover': {
    borderColor: '#4caf50',
    backgroundColor: selected ? '#e8f5e9' : '#f5f5f5'
  }
}));

const PinInputContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  justifyContent: 'center',
  marginTop: '24px',
  marginBottom: '24px'
});

const PinDigitInput = styled(TextField)({
  width: '50px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: '24px',
    fontWeight: 700,
    textAlign: 'center'
  },
  '& input': {
    textAlign: 'center',
    padding: '12px 8px'
  }
});

const InfoCard = styled(Box)({
  padding: '16px',
  background: 'linear-gradient(135deg, #e3f2fd 0%, #f3f9ff 100%)',
  borderRadius: '12px',
  marginBottom: '16px',
  border: '1px solid #90caf9'
});

// ============================================
// Step Configuration
// ============================================

const STEPS = ['Choose Method', 'Request Verification', 'Enter PIN'];

const METHOD_ICONS = {
  'ADDRESS': <LocationOn />,
  'PHONE_CALL': <Phone />,
  'SMS': <Sms />,
  'EMAIL': <Email />,
  'AUTO': <VerifiedUser />
};

const METHOD_LABELS = {
  'ADDRESS': 'Postcard by Mail',
  'PHONE_CALL': 'Phone Call',
  'SMS': 'Text Message (SMS)',
  'EMAIL': 'Email',
  'AUTO': 'Instant Verification'
};

const METHOD_DESCRIPTIONS = {
  'ADDRESS': 'We\'ll send a postcard with a verification code to your business address. Takes 5-14 days.',
  'PHONE_CALL': 'Receive an automated call with your verification code at your business phone number.',
  'SMS': 'Receive a text message with your verification code.',
  'EMAIL': 'Receive an email with your verification code.',
  'AUTO': 'Instant verification based on your business information.'
};

// ============================================
// Main Component
// ============================================

const VerificationPopup = ({
  task,
  onComplete,
  onBack,
  businessProfileData = {},
  profileId
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Step state
  const [activeStep, setActiveStep] = useState(0);

  // Verification options
  const [verificationOptions, setVerificationOptions] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);

  // Verification request state
  const [verificationId, setVerificationId] = useState(null);
  const [pendingVerification, setPendingVerification] = useState(null);

  // PIN input state
  const [pinDigits, setPinDigits] = useState(['', '', '', '', '', '']);

  // Contact info for certain methods
  const [contactName, setContactName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');

  // Business info
  const businessName = businessProfileData?.title || businessProfileData?.name || 'Your Business';
  const businessAddress = businessProfileData?.storefrontAddress?.addressLines?.join(', ') ||
                          businessProfileData?.address?.addressLines?.join(', ') ||
                          '';
  const businessPhone = businessProfileData?.phoneNumbers?.primaryPhone || '';

  // Load verification options on mount
  useEffect(() => {
    loadVerificationOptions();
  }, [profileId]);

  const loadVerificationOptions = async () => {
    try {
      setLoading(true);
      setError('');

      const locationId = profileId ||
                        businessProfileData?.name?.split('/').pop() ||
                        businessProfileData?.locationId;

      if (!locationId) {
        throw new Error('Location ID not found');
      }

      // First, check if there's a pending verification
      const verificationsResult = await GMBService.listVerifications(locationId);
      if (verificationsResult.success && verificationsResult.pendingVerification) {
        setPendingVerification(verificationsResult.pendingVerification);
        setVerificationId(verificationsResult.pendingVerification.name?.split('/').pop());
        setActiveStep(2); // Jump to PIN entry
        setLoading(false);
        return;
      }

      // Fetch available verification options
      const result = await GMBService.fetchVerificationOptions(locationId);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch verification options');
      }

      if (result.options.length === 0) {
        setError('No verification methods available for this business. Please ensure your business information is complete.');
      } else {
        setVerificationOptions(result.options);
        // Auto-select first option
        if (result.options.length > 0) {
          setSelectedMethod(result.options[0].method);
          // Pre-fill phone if available
          if (result.options[0].phoneNumber) {
            setPhoneNumber(result.options[0].phoneNumber);
          }
        }
      }
    } catch (err) {
      logger.error('[VerificationPopup] Error loading options:', err);
      setError(err.message || 'Failed to load verification options');
    } finally {
      setLoading(false);
    }
  };

  // Handle method selection
  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    const option = verificationOptions.find(o => o.method === method);
    if (option?.phoneNumber) {
      setPhoneNumber(option.phoneNumber);
    }
    if (option?.emailData?.emailAddress) {
      setEmailAddress(option.emailData.emailAddress);
    }
  };

  // Request verification
  const handleRequestVerification = async () => {
    try {
      setSubmitting(true);
      setError('');

      const locationId = profileId ||
                        businessProfileData?.name?.split('/').pop() ||
                        businessProfileData?.locationId;

      if (!locationId) {
        throw new Error('Location ID not found');
      }

      // Build verification data based on method
      const verificationData = {};
      if (selectedMethod === 'ADDRESS') {
        verificationData.mailerContactName = contactName || businessName;
      } else if (selectedMethod === 'PHONE_CALL' || selectedMethod === 'SMS') {
        verificationData.phoneNumber = phoneNumber || businessPhone;
      } else if (selectedMethod === 'EMAIL') {
        verificationData.emailAddress = emailAddress;
      }

      logger.log('[VerificationPopup] Requesting verification:', { method: selectedMethod, verificationData });

      const result = await GMBService.requestVerification(locationId, selectedMethod, verificationData);

      if (!result.success) {
        throw new Error(result.error || 'Failed to request verification');
      }

      // Store verification ID for completion
      const verId = result.verification?.name?.split('/').pop() || result.verification?.verificationId;
      setVerificationId(verId);
      setPendingVerification(result.verification);

      // Move to PIN entry step
      setActiveStep(2);

    } catch (err) {
      logger.error('[VerificationPopup] Error requesting verification:', err);
      setError(err.message || 'Failed to request verification');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle PIN digit input
  const handlePinChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste - distribute digits across inputs
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newPinDigits = [...pinDigits];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newPinDigits[index + i] = digit;
        }
      });
      setPinDigits(newPinDigits);

      // Focus next empty input or last input
      const nextEmptyIndex = newPinDigits.findIndex((d, i) => i >= index && d === '');
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      document.getElementById(`pin-${focusIndex}`)?.focus();
    } else {
      // Single character input
      const newPinDigits = [...pinDigits];
      newPinDigits[index] = value.replace(/\D/g, '');
      setPinDigits(newPinDigits);

      // Auto-focus next input
      if (value && index < 5) {
        document.getElementById(`pin-${index + 1}`)?.focus();
      }
    }
  };

  // Handle PIN backspace
  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      document.getElementById(`pin-${index - 1}`)?.focus();
    }
  };

  // Complete verification with PIN
  const handleCompleteVerification = async () => {
    const pin = pinDigits.join('');
    if (pin.length !== 5 && pin.length !== 6) {
      setError('Please enter the complete verification PIN');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const locationId = profileId ||
                        businessProfileData?.name?.split('/').pop() ||
                        businessProfileData?.locationId;

      if (!locationId || !verificationId) {
        throw new Error('Verification information is missing');
      }

      logger.log('[VerificationPopup] Completing verification:', { locationId, verificationId, pin });

      const result = await GMBService.completeVerification(locationId, verificationId, pin);

      if (!result.success) {
        throw new Error(result.error || 'Failed to verify PIN');
      }

      setSuccess(true);

    } catch (err) {
      logger.error('[VerificationPopup] Error completing verification:', err);
      setError(err.message || 'Failed to verify PIN. Please check the code and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success State
  if (success) {
    return (
      <Box>
        <HeaderContainer sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)' }}>
          <IconContainer>
            <CheckCircle sx={{ fontSize: 40 }} />
          </IconContainer>
          <Typography variant="h5" fontWeight={700}>
            Verified Successfully!
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            {businessName} is now verified on Google
          </Typography>
        </HeaderContainer>
        <ContentContainer>
          <Alert severity="success" sx={{ mb: 3 }}>
            Congratulations! Your business profile is now verified. It may take a few minutes for the verification status to update across all Google services.
          </Alert>

          <InfoCard>
            <Typography variant="subtitle2" fontWeight={600} color="primary" gutterBottom>
              What happens next?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Your business will appear on Google Search and Maps<br />
              • You can respond to reviews<br />
              • You can post updates and offers<br />
              • Your business insights will be available
            </Typography>
          </InfoCard>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <ActionButton
              variant="primary"
              onClick={() => onComplete?.({ completed: true, taskId: task?.id })}
              startIcon={<CheckCircle />}
            >
              Done
            </ActionButton>
          </Box>
        </ContentContainer>
      </Box>
    );
  }

  // Loading State
  if (loading) {
    return (
      <Box>
        <HeaderContainer>
          <IconContainer>
            <VerifiedUser sx={{ fontSize: 36 }} />
          </IconContainer>
          <Typography variant="h5" fontWeight={700}>
            Verify Your Business
          </Typography>
        </HeaderContainer>
        <ContentContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={48} sx={{ color: '#4caf50', mb: 2 }} />
            <Typography color="text.secondary">
              Loading verification options...
            </Typography>
          </Box>
        </ContentContainer>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <HeaderContainer>
        <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
          <IconButton onClick={onBack} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
        </Box>
        <IconContainer>
          <VerifiedUser sx={{ fontSize: 36 }} />
        </IconContainer>
        <Typography variant="h5" fontWeight={700}>
          Verify Your Business
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          {businessName}
        </Typography>
      </HeaderContainer>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content */}
      <ContentContainer>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Step 0: Choose Method */}
        {activeStep === 0 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Choose a verification method
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select how you'd like to receive your verification code
            </Typography>

            {verificationOptions.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No verification methods available. Please ensure your business address and contact information are complete.
              </Alert>
            ) : (
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup value={selectedMethod} onChange={(e) => handleMethodSelect(e.target.value)}>
                  {verificationOptions.map((option) => (
                    <MethodCard
                      key={option.method}
                      selected={selectedMethod === option.method}
                      onClick={() => handleMethodSelect(option.method)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Radio
                          value={option.method}
                          checked={selectedMethod === option.method}
                          sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }}
                        />
                        <Box sx={{ color: '#4caf50', mt: 0.5 }}>
                          {METHOD_ICONS[option.method] || <VerifiedUser />}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {METHOD_LABELS[option.method] || option.method}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {METHOD_DESCRIPTIONS[option.method]}
                          </Typography>
                          {option.phoneNumber && (
                            <Chip
                              size="small"
                              icon={<Phone sx={{ fontSize: 14 }} />}
                              label={option.phoneNumber}
                              sx={{ mt: 1, fontSize: '12px' }}
                            />
                          )}
                          {option.emailData?.emailAddress && (
                            <Chip
                              size="small"
                              icon={<Email sx={{ fontSize: 14 }} />}
                              label={option.emailData.emailAddress}
                              sx={{ mt: 1, fontSize: '12px' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </MethodCard>
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            {/* Method-specific inputs */}
            {selectedMethod === 'ADDRESS' && (
              <TextField
                fullWidth
                label="Contact Name (for postcard)"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder={businessName}
                sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            )}

            {(selectedMethod === 'PHONE_CALL' || selectedMethod === 'SMS') && !verificationOptions.find(o => o.method === selectedMethod)?.phoneNumber && (
              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={businessPhone || '+91 XXXXXXXXXX'}
                sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  )
                }}
              />
            )}

            {selectedMethod === 'EMAIL' && !verificationOptions.find(o => o.method === selectedMethod)?.emailData?.emailAddress && (
              <TextField
                fullWidth
                label="Email Address"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                type="email"
                sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  )
                }}
              />
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <ActionButton variant="secondary" onClick={onBack}>
                Cancel
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={() => setActiveStep(1)}
                disabled={!selectedMethod}
              >
                Continue
              </ActionButton>
            </Box>
          </>
        )}

        {/* Step 1: Confirm and Request */}
        {activeStep === 1 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Confirm Verification Request
            </Typography>

            <InfoCard sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ color: '#4caf50' }}>
                  {METHOD_ICONS[selectedMethod]}
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {METHOD_LABELS[selectedMethod]}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {METHOD_DESCRIPTIONS[selectedMethod]}
                  </Typography>
                </Box>
              </Box>

              {selectedMethod === 'ADDRESS' && businessAddress && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: '8px' }}>
                  <Typography variant="caption" color="text.secondary">
                    Postcard will be sent to:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                    {contactName || businessName}<br />
                    {businessAddress}
                  </Typography>
                </Box>
              )}

              {(selectedMethod === 'PHONE_CALL' || selectedMethod === 'SMS') && phoneNumber && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: '8px' }}>
                  <Typography variant="caption" color="text.secondary">
                    Code will be sent to:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                    {phoneNumber}
                  </Typography>
                </Box>
              )}

              {selectedMethod === 'EMAIL' && emailAddress && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: '8px' }}>
                  <Typography variant="caption" color="text.secondary">
                    Code will be sent to:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                    {emailAddress}
                  </Typography>
                </Box>
              )}
            </InfoCard>

            {selectedMethod === 'ADDRESS' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Info sx={{ mr: 1 }} />
                The postcard typically arrives within 5-14 business days. Make sure someone is available to receive mail at this address.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <ActionButton variant="secondary" onClick={() => setActiveStep(0)}>
                Back
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={handleRequestVerification}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <VerifiedUser />}
              >
                {submitting ? 'Requesting...' : 'Request Verification'}
              </ActionButton>
            </Box>
          </>
        )}

        {/* Step 2: Enter PIN */}
        {activeStep === 2 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom align="center">
              Enter Verification Code
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              {selectedMethod === 'ADDRESS'
                ? 'Enter the 5-digit code from your postcard'
                : selectedMethod === 'PHONE_CALL'
                  ? 'Enter the code from your phone call'
                  : selectedMethod === 'SMS'
                    ? 'Enter the code from your text message'
                    : selectedMethod === 'EMAIL'
                      ? 'Enter the code from your email'
                      : 'Enter the verification code you received'}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Lock sx={{ color: '#4caf50', fontSize: 48 }} />
            </Box>

            <PinInputContainer>
              {pinDigits.slice(0, 5).map((digit, index) => (
                <PinDigitInput
                  key={index}
                  id={`pin-${index}`}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                  inputProps={{ maxLength: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                  variant="outlined"
                />
              ))}
            </PinInputContainer>

            {pendingVerification && (
              <InfoCard>
                <Typography variant="body2" color="text.secondary" align="center">
                  Verification requested via <strong>{METHOD_LABELS[pendingVerification.method || selectedMethod]}</strong>
                  {pendingVerification.createTime && (
                    <> on {new Date(pendingVerification.createTime).toLocaleDateString()}</>
                  )}
                </Typography>
              </InfoCard>
            )}

            <Box sx={{ textAlign: 'center', mt: 2, mb: 3 }}>
              <Button
                variant="text"
                startIcon={<Refresh />}
                onClick={loadVerificationOptions}
                sx={{ textTransform: 'none' }}
              >
                Didn't receive the code? Try again
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <ActionButton variant="secondary" onClick={() => setActiveStep(0)}>
                Back
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={handleCompleteVerification}
                disabled={submitting || pinDigits.filter(d => d).length < 5}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
              >
                {submitting ? 'Verifying...' : 'Verify'}
              </ActionButton>
            </Box>
          </>
        )}
      </ContentContainer>
    </Box>
  );
};

export default VerificationPopup;
